import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { PHONE, PHONE_TEL, EMAIL } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";

const SERVICES = [
  ["managed-it", "Managed IT"], ["dispatch", "Managed Dispatch"],
  ["onsite-support", "Onsite Support (Nationwide)"], ["not-sure", "Not sure yet"],
];
const NEED_MAP = { it: "managed-it", dispatch: "dispatch" };
const VALID = SERVICES.map(([v]) => v);
const EMPTY = { name: "", email: "", company: "", phone: "", message: "", website: "" };

const MapPlaceholder = () => (
  <div data-testid="map-placeholder" className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-[#0E2657]">
    <div className="grid-lines absolute inset-0" />
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 400 300" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 210 C 90 180, 140 240, 220 170 S 340 110, 400 140" stroke="#1777AF" strokeWidth="2" fill="none" strokeDasharray="6 6" />
      <path d="M40 0 L 120 300 M 260 0 L 300 300" stroke="rgba(41,193,211,0.15)" strokeWidth="10" />
    </svg>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
      <span className="relative mx-auto grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#1777AF] to-[#29C1D3] text-white shadow-[0_0_40px_rgba(41,193,211,0.5)]">
        <MapPin className="h-6 w-6" />
      </span>
      <p className="mt-4 rounded-full bg-white/10 px-4 py-1.5 text-sm text-white backdrop-blur">[Office address — map placeholder]</p>
    </div>
  </div>
);

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [service, setService] = useState(() => {
    const need = NEED_MAP[params.get("need")] || params.get("need");
    return VALID.includes(need) ? need : "not-sure";
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  useSeo("Contact | Get a Free Consultation | Bodhatek", "Book a free consultation with Bodhatek for affordable managed IT, dispatch and nationwide onsite support.");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, { ...form, phone: form.phone || null, service });
      setDone(true);
      setForm(EMPTY);
      toast.success("Thanks! We'll be in touch within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? "Please check the highlighted fields and try again." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = "h-12 rounded-xl border-slate-200 bg-white focus-visible:ring-[#1777AF]";

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        lines={["Get a free", { text: "consultation.", className: "text-grad" }]}
        sub="Tell us what's on your plate. A senior engineer — not a sales bot — will reply within one business day."
      />
      <section className="bg-[#EAF0F7] py-20 sm:py-28">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-4">
              <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone" className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#1777AF]/40">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#0E2657] text-cyan-300"><Phone className="h-5 w-5" /></span>
                <span><span className="block text-xs text-slate-500">Call 24/7</span><span className="font-semibold text-slate-900">{PHONE}</span></span>
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#1777AF]/40">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#0E2657] text-cyan-300"><Mail className="h-5 w-5" /></span>
                <span><span className="block text-xs text-slate-500">Email</span><span className="font-semibold text-slate-900">{EMAIL}</span></span>
              </a>
              <MapPlaceholder />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-10">
              {done ? (
                <motion.div data-testid="contact-success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex min-h-[420px] flex-col items-start justify-center">
                  <CheckCircle2 className="h-14 w-14 text-emerald-500" />
                  <h2 className="mt-6 font-display text-4xl font-bold tracking-tight text-slate-900">Request received.</h2>
                  <p className="mt-3 max-w-md text-slate-600">Thanks for reaching out. A Bodhatek specialist will contact you within one business day.</p>
                  <button data-testid="contact-send-another" onClick={() => setDone(false)} className="btn-ghost mt-8 border-slate-300 text-slate-900 hover:border-[#1777AF] hover:text-[#1777AF]">Send another request</button>
                </motion.div>
              ) : (
                <form onSubmit={submit} data-testid="consultation-form" className="grid gap-5 sm:grid-cols-2">
                  <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:col-span-2">Tell us about your needs</h2>
                  <div className="space-y-2">
                    <Label htmlFor="name">Full name *</Label>
                    <Input id="name" required maxLength={100} value={form.name} onChange={set("name")} className={field} data-testid="contact-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Work email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company *</Label>
                    <Input id="company" required maxLength={120} value={form.company} onChange={set("company")} className={field} data-testid="contact-company-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" maxLength={40} value={form.phone} onChange={set("phone")} className={field} data-testid="contact-phone-input" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Service interest</Label>
                    <Select value={service} onValueChange={setService}>
                      <SelectTrigger className={field} data-testid="contact-service-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SERVICES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`service-option-${v}`}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="message">How can we help? *</Label>
                    <Textarea id="message" required maxLength={3000} rows={5} value={form.message} onChange={set("message")} className="rounded-xl border-slate-200 focus-visible:ring-[#1777AF]" data-testid="contact-message-input" />
                  </div>
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
                  <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-slate-500">We'll only use your details to respond to this request.</p>
                    <button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-primary disabled:opacity-70">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Get my free consultation <ArrowUpRight className="h-4 w-4" /></>}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
